import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { PCImage } from 'app/design/primitives/image';
import React from 'react';
import type { Loan } from 'app/types/loan';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';

import { LoanETA } from 'app/types/loan';
import { Separator } from 'app/design/primitives/text-divider';
import { Label } from 'app/design/icons/label';
import { ChatBubble } from 'app/design/icons/chat-bubble';
import { useNavigation } from 'app/hooks/navigation';
import type { User } from 'app/types/user';
import { getLoanInfos } from '../functions';
import { useUser } from 'app/network/user';
import { useMyProfile } from 'app/network/profile';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { LoanFeeModals, useLoanFeeModals } from 'app/screens/management/loan-fee';
import { Clubs } from 'app/design/icons/clubs';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconAlertTriangle, IconChevronRight, IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface DetailsSectionProps {
  loan?: Loan | null;
  user?: User;
}

function ParticipantsSection({
  loan,
  isLender,
  t,
}: {
  loan?: Loan | null;
  isLender: boolean;
  t: TFunction<'one-loan'>;
}) {
  if (
    !loan?.listing.maxParticipants ||
    loan.listing.numParticipants === undefined ||
    loan.listing.numParticipants === null
  ) {
    return null;
  }

  return (
    <Row>
      <Column gap="spacing.general.x2">
        <Text fontWeight="700" variant="bodyBase">
          {t(isLender ? 'myParticipations' : 'allParticipations')}
        </Text>
        <Row gap="spacing.general.x1" alignItems="center">
          {isLender ? (
            <Text style={{ color: theme.colors['text.secondary'] }} variant="bodyBase">
              {loan.numParticipants}
            </Text>
          ) : (
            <>
              <Text
                color={loan.listing.numParticipants > 0 ? 'text.primary' : 'text.primary'}
                style={{
                  backgroundColor:
                    loan.listing.numParticipants > 0
                      ? theme.colors['surface.brand.secondary-1.default']
                      : theme.colors['surface.brand.secondary-2.default'],
                  paddingHorizontal: theme.spacing['spacing.general.x2'],
                  paddingVertical: 4,
                  borderRadius: theme.borderRadii['radius.sm'],
                }}
              >
                {loan.listing.numParticipants >= loan.listing.maxParticipants
                  ? t('subscriptionsSection.participantsFull', { ns: 'one-listing' })
                  : t('subscriptionsSection.participants', {
                      ns: 'one-listing',
                      count: loan.listing.numParticipants,
                    })}
              </Text>
              <Text style={{ color: theme.colors['text.secondary'] }} variant="bodyBase">
                {`/ ${loan.listing.maxParticipants.toFixed(0)}`}
              </Text>
            </>
          )}
        </Row>
      </Column>
    </Row>
  );
}

function LoanFeeSection({ loan, onPress, t }: { loan?: Loan | null; onPress: () => void; t: TFunction<'one-loan'> }) {
  if (!loan?.fee) {
    return null;
  }

  return (
    <Pressable onPress={onPress}>
      <Column
        gap="spacing.general.x0,5"
        style={{
          backgroundColor: theme.colors['surface.tertiary-neutral'],
          padding: theme.spacing['spacing.general.x4'],
          borderRadius: theme.borderRadii['radius.lg'],
        }}
      >
        <Row alignItems="center" gap="spacing.general.x0,5">
          <Text fontWeight="700" variant="bodyBase">
            {t('loanFee.title', {
              ns: 'one-listing',
              amount: t('x$', { ns: 'common', amount: loan.fee.toFixed(loan.fee % 1 === 0 ? 0 : 2) }),
            })}
          </Text>
          <View style={{ marginVertical: -8 }}>
            <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color="transparent" />
          </View>
        </Row>
        {loan.feeDescription ? <Text variant="bodyBase">{loan.feeDescription}</Text> : null}
        <Text variant="bodyBase">
          {t('fields.loanFeePaymentMethodLabel', { ns: 'loanFee' })} :{' '}
          {loan.feePaymentMethod ? t(`paymentMethods.${loan.feePaymentMethod}`, { ns: 'loanFee' }) : t('N/A')}
        </Text>
      </Column>
    </Pressable>
  );
}

function CounterpartAction({
  isLender,
  loan,
  onOpenChat,
  onOpenParticipants,
  otherUser,
  t,
}: {
  isLender: boolean;
  loan?: Loan | null;
  onOpenChat: () => void;
  onOpenParticipants: () => void;
  otherUser?: Loan['borrower'];
  t: TFunction<'one-loan'>;
}) {
  if (loan?.listing.maxParticipants && !isLender) {
    return (
      <Pressable onPress={onOpenParticipants}>
        <Row alignItems="center" gap="spacing.general.x5">
          <Clubs />
          <Text flex={1} variant="bodyBase">
            {t('listParticipants')}
          </Text>
          <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
        </Row>
      </Pressable>
    );
  }

  if (!otherUser) {
    return null;
  }

  return (
    <Pressable onPress={onOpenChat}>
      <Row alignItems="center" gap="spacing.general.x5">
        {otherUser.profile ? (
          <PCImage
            aspectRatio={1}
            height={30}
            source={{ uri: otherUser.profile.picture }}
            style={{ borderRadius: 15 }}
          />
        ) : (
          <ChatBubble />
        )}
        <Text flex={1} variant="bodyBase">
          {t('sendMessage', {
            name: !otherUser.profile
              ? t('deletedUser', { ns: 'common' })
              : `${otherUser.profile.firstName} ${otherUser.profile.lastName}`,
          })}
        </Text>
        <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
      </Row>
    </Pressable>
  );
}

function IncidentAction({ onPress, t }: { onPress: () => void; t: TFunction<'one-loan'> }) {
  return (
    <Pressable onPress={onPress}>
      <Row alignItems="center" gap="spacing.general.x5">
        <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} />
        <Text flex={1} variant="bodyBase">
          {t('contactSupportAboutIncident')}
        </Text>
        <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
      </Row>
    </Pressable>
  );
}

export function Details({ loan, user }: DetailsSectionProps) {
  const { t } = useTranslation('one-loan');
  const navigation = useNavigation();
  const { data: myself } = useUser();
  const { data: myProfile } = useMyProfile();

  const { loanStatus, otherUser } = (() => {
    return getLoanInfos(loan, user);
  })();

  const isLender = loan?.lenderId === myself?.id;

  const isService = loan?.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan?.listing);
  const isSell = loan?.listing.transactionMode === TransactionMode.sell;

  const isInsured = (myself?.insured || user?.insured) && !isService && !isDonation && !isSell;

  const loanFeeModalsState = useLoanFeeModals();

  const openParticipants = () => {
    if (loan) {
      navigation.navigate('loan-participants', { loanId: loan.id }, { pop: true });
    }
  };
  const openChat = () => {
    if (loan) {
      navigateToChat(navigation, loan.channelURL);
    }
  };
  const reportIncident = () => {
    navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
      i18nKey: `contactSupportAboutIncidentPrompt.${isInsured ? 'insured' : 'uninsured'}`,
      ns: 'one-loan',
      values: { name: myProfile!.firstName },
    });
  };

  return (
    <>
      <Column gap="spacing.general.x4">
        <ParticipantsSection loan={loan} isLender={isLender} t={t} />

        <LoanFeeSection loan={loan} onPress={() => loanFeeModalsState.setIsLoanFeeOtherModalOpen(true)} t={t} />

        <Separator color={theme.colors['surface.tertiary-neutral']} />

        <Pressable onPress={() => (!loan ? undefined : navigateToListing(navigation, loan.listing.id))}>
          <Row alignItems="center" gap="spacing.general.x5">
            <Label />
            <Text flex={1} variant="bodyBase">
              {t('viewListing')}
            </Text>
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
          </Row>
        </Pressable>

        {otherUser ? <Separator color={theme.colors['surface.tertiary-neutral']} /> : null}

        <CounterpartAction
          isLender={isLender}
          loan={loan}
          onOpenChat={openChat}
          onOpenParticipants={openParticipants}
          otherUser={otherUser}
          t={t}
        />

        {loanStatus !== LoanETA.pending ? <Separator color={theme.colors['surface.tertiary-neutral']} /> : null}

        {loanStatus !== LoanETA.pending ? <IncidentAction onPress={reportIncident} t={t} /> : null}
      </Column>
      <LoanFeeModals state={loanFeeModalsState} />
    </>
  );
}
